<template>
  <div
    class="book-category-page pb-9 px-4 px-sm-6 px-md-6 px-lg-6 px-xl-6 pt-0 mx-auto "
  >
    <div class="category-page-filter-wrapper">
      <CategoryFilter />
      <LibraryFilter
        class="category-page-filter"
        :searcher="searcher"
        :show-extra="showExtra"
        @change="filterChanged"
      />
    </div>

    <div
      class="d-flex "
      :class="{
        'flex-row': mediaSize?.wide,
        'flex-column': mediaSize?.normal || mediaSize?.narrow
      }"
    >
      <div
        class="category-publication-info-block py-4 pa-lg-4 pa-xl-4 pa-xxl-4"
      >
        <div class="wide-category-name-block d-flex justify-space-between pb-3">
          <div class="category-page-title d-flex flex-row">
            <CategoryIcon :category="category" />
            <div>{{ label }}</div>
          </div>
          <div
            v-if="mediaSize?.wide"
            class="category-page-book-number d-flex justify-end"
          >
            {{ $t('CategoryPage.booksCounter.label', { booksCounter }) }}
          </div>
        </div>
        <div
          v-if="mediaSize?.normal || mediaSize?.narrow"
          class="normal-narrow-category-name-block d-flex flex-column"
        >
          <CategoryInfo v-if="!isSuggested" :category="category" />
          <div class="search-results-controls-block pb-4">
            <div
              class="search-results-controls-wrapper"
              :class="segmentedSwitcher"
            >
              <div class="active-bg"></div>
              <button
                v-for="(item, index) in switcherControls"
                :key="index"
                :class="{
                  active: item.paraName === segmentedSwitcher
                }"
                @click="switcherHandler(item.paraName)"
              >
                <span>{{ item.name }}</span>
              </button>
            </div>
          </div>
        </div>
        <div class="d-flex flex-column">
          <div
            v-if="segmentedSwitcher === SwitcherEnum.START_POSITION"
            class="controls-block d-flex justify-space-between py-4"
          >
            <div class="d-flex flex-row align-center">
              <div class="sort-label-selector me-1">
                {{ $t('CategoryPage.sort.label') }}
              </div>
              <div>
                <v-select
                  v-model="selectedSorting"
                  class="sort-selector"
                  :items="sortBooksOptions"
                  hide-details
                ></v-select>
              </div>
            </div>
            <div class="d-flex flex-row">
              <BaseButton
                v-if="mediaSize?.normal"
                class="publication-view-button me-1"
                elevation="0"
                fab
                small
                color="white"
                @click="selectedPubView = publicationViewEnum.ONE"
              >
                <BaseSpriteIcon
                  icon-name="ico-grid-2"
                  :color="getColorPubViewSelection(publicationViewEnum.ONE)"
                />
              </BaseButton>
              <BaseButton
                v-if="mediaSize?.normal || mediaSize?.wide"
                class="publication-view-button me-1"
                elevation="0"
                fab
                small
                color="white"
                @click="selectedPubView = publicationViewEnum.TWO"
              >
                <BaseSpriteIcon
                  icon-name="ico-grid-4"
                  :color="getColorPubViewSelection(publicationViewEnum.TWO)"
                />
              </BaseButton>
              <BaseButton
                v-if="mediaSize?.wide"
                class="publication-view-button"
                elevation="0"
                fab
                small
                color="white"
                @click="selectedPubView = publicationViewEnum.THREE"
              >
                <BaseSpriteIcon
                  icon-name="ico-grid-6"
                  :color="getColorPubViewSelection(publicationViewEnum.THREE)"
                />
              </BaseButton>
            </div>
          </div>
          <div
            v-if="
              segmentedSwitcher === SwitcherEnum.START_POSITION ||
                mediaSize?.wide
            "
          >
            <div
              v-if="publications.length"
              class="publications-list"
              :class="[publicationViewClass]"
            >
              <v-lazy
                v-for="(pub, index) in sortedPublications"
                :key="`${pub.id}_${index}`"
                :value="index < preloadItems"
                class="publications-item"
                :min-height="
                  selectedPubView === publicationViewEnum.TWO ||
                  selectedPubView === publicationViewEnum.ONE
                    ? 146
                    : 96
                "
              >
                <PublicationCategoryItem
                  :publication="pub"
                  :description="
                    selectedPubView === publicationViewEnum.TWO ||
                      selectedPubView === publicationViewEnum.ONE
                  "
                  :wide-description="
                    selectedPubView === publicationViewEnum.ONE
                  "
                  :cover-for-narrow-view="
                    selectedPubView === publicationViewEnum.ONE
                  "
                  :show-categories="showCategories"
                  :show-author-link="showAuthorLink"
                />
              </v-lazy>
            </div>
            <div v-else class="no-books-block pa-6">
              <div><img :src="noBooksImg" alt="no books" /></div>
              <div>{{ $t('LibraryPage.placeholder.noBooks') }}</div>
            </div>
          </div>
          <DiscoveryBlock
            v-if="
              segmentedSwitcher === SwitcherEnum.MID_POSITION &&
                !mediaSize?.wide
            "
            hide-about
            :category="category"
            class="normal-narrow-discovery-block"
          />
        </div>
      </div>

      <DiscoveryBlock
        v-if="mediaSize?.wide"
        :category="category"
        class="wide-discovery-block pa-4"
      />
    </div>
  </div>
</template>

<script>
import images from '@/assets/images';
import publicationUtils from '@/services/utils/publicationUtils';

import AppStateEnum from '@/enums/AppStateEnum';

import PublicationCategoryItem from '@/components/views/CategoryView/PublicationCategoryItem/PublicationCategoryItem.vue';
import CategoryInfo from '@/components/views/CategoryView/CategoryInfo/CategoryInfo.vue';
import DiscoveryBlock from '@/components/views/CategoryView/DiscoveryBlock/DiscoveryBlock.vue';
import CategoryFilter from '@/components/views/CategoryFilter/CategoryFilter.vue';
import BaseSpriteIcon from '@/components/base/BaseSpriteIcon/BaseSpriteIcon.vue';
import BaseButton from '@/components/base/BaseButton/BaseButton.vue';

import CategoryIcon from '@/components/controls/CategoryIcon/CategoryIcon.vue';
import CustomCategoriesEnum from '@shared/enums/CustomCategoriesEnum';

const publicationViewEnum = {
  ONE: 'one',
  TWO: 'two',
  THREE: 'three'
};

const SwitcherEnum = {
  START_POSITION: '-start-position',
  MID_POSITION: '-mid-position'
};

export default {
  name: 'BookCategoryLayout',
  components: {
    PublicationCategoryItem,
    LibraryFilter: () =>
      import('@/components/views/LibraryFilter/LibraryFilter.vue'),
    CategoryFilter,
    CategoryInfo,
    BaseSpriteIcon,
    BaseButton,
    CategoryIcon,
    DiscoveryBlock
  },
  props: {
    category: String,
    label: String,
    searcher: Object,
    publications: Array,
    customBackTo: Object,
    customCounter: [Number, String],
    countCollectionBooks: {
      type: Boolean,
      default: true
    },
    showExtra: {
      type: Boolean,
      default: false
    },
    showCategories: {
      type: Boolean,
      default: false
    },
    showGenres: {
      type: Boolean,
      default: true
    },
    showAuthorLink: {
      type: Boolean,
      default: true
    },
    sortStrategy: {
      type: String,
      default: publicationUtils.sortPublicationEnum.ALPHABET
    },
    sortOptions: Object
  },
  data() {
    const lang = this.$store.getters['ContextStore/currentLanguageGetter'];
    const defaultRoute =
      lang === 'en'
        ? { name: AppStateEnum.MANAGE_PUBLICATION }
        : {
            name: AppStateEnum.MANAGE_PUBLICATION_LANGUAGE,
            params: { pathMatch: lang }
          };

    return {
      isMounted: false,
      selectedSorting: this.sortStrategy,
      sortBooksOptions: [
        {
          text: 'Newest',
          value: publicationUtils.sortPublicationEnum.PUBLISH_DATE
        },
        {
          text: 'By Title',
          value: publicationUtils.sortPublicationEnum.ALPHABET
        },
        {
          text: 'By Author',
          value: publicationUtils.sortPublicationEnum.AUTHOR
        }
      ],
      segmentedSwitcher: SwitcherEnum.START_POSITION,
      SwitcherEnum,

      selectedPubView: publicationViewEnum.TWO,
      publicationViewEnum,

      filterText: '',
      defaultRoute,
      noBooksImg: images.picSearch
    };
  },
  computed: {
    isSuggested() {
      return this.category === CustomCategoriesEnum.SUGGESTED_BOOK;
    },
    publicationViewClass() {
      const classMap = {
        [publicationViewEnum.ONE]: 'publication-view-one',
        [publicationViewEnum.TWO]: 'publication-view-two',
        [publicationViewEnum.THREE]: 'publication-view-three'
      };

      return classMap[this.selectedPubView] || 'publication-view-two';
    },
    switcherControls() {
      const controls = [
        {
          name: this.$t('CategoryPage.booksCounterTab.label', {
            booksCounter: this.booksCounter
          }),
          paraName: SwitcherEnum.START_POSITION
        },
        {
          name: this.$t('CategoryPage.discoverTab.label'),
          paraName: SwitcherEnum.MID_POSITION
        }
      ];
      return controls;
    },
    backTo() {
      return this.customBackTo || this.defaultRoute;
    },
    sortedPublications() {
      const sortedPubs = publicationUtils.sortPublication(
        this.publications,
        this.selectedSorting,
        this.sortOptions
      );
      return sortedPubs;
    },
    searchString() {
      return this.searcher?.term ?? '';
    },
    booksCounter() {
      if (this.customCounter) {
        return this.customCounter;
      }
      if (!this.countCollectionBooks) {
        return this.publications?.length;
      }
      return publicationUtils.countPublicationItems(
        this.publications,
        !this.searcher?.isCollectionIncluded
      );
    },
    mediaSize() {
      const mediaSize = this.$store.getters['MediaDetectorStore/mediaSize'];
      if (!this.isMounted) {
        return { ...mediaSize, ...{ wide: true } };
      }
      return mediaSize;
    },
    preloadItems() {
      const { narrow, normal } = this.mediaSize;
      let items = 15;
      switch (true) {
        case narrow:
          items = 6;
          break;
        case normal:
          items = 12;
          break;
      }
      return items;
    }
  },
  watch: {
    mediaSize: {
      deep: true,
      async handler(newVal) {
        if (newVal.wide && this.selectedPubView === publicationViewEnum.ONE) {
          this.selectedPubView = publicationViewEnum.TWO;
        }
        if (
          newVal.normal &&
          this.selectedPubView === publicationViewEnum.THREE
        ) {
          this.selectedPubView = publicationViewEnum.TWO;
        }
        if (newVal.narrow) {
          this.selectedPubView = publicationViewEnum.ONE;
        }
      }
    }
  },
  mounted() {
    this.isMounted = true;
  },
  methods: {
    switcherHandler(paraName) {
      this.segmentedSwitcher = paraName;
    },

    getColorPubViewSelection(publicationView) {
      return this.selectedPubView === publicationView ? '#0F172A' : '#94A3B8';
    },
    filterChanged(searcher) {
      this.$emit('change', searcher);
    }
  }
};
</script>

<style lang="less" src="./BookCategoryLayout.less"></style>
